import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HUB_PREVIEW_URL = 'https://sso-engine-1.preview.emergentagent.com';
const HUB_PROD_URL = 'https://controlhub.blackscott.co.za';

// Token-based auth via Authorization header
const getStoredToken = () => localStorage.getItem('access_token');
const storeToken = (token) => { if (token) localStorage.setItem('access_token', token); };
const clearToken = () => localStorage.removeItem('access_token');

// Tenant branding
const getStoredBranding = () => {
  try { return JSON.parse(localStorage.getItem('tenant_branding') || 'null'); } catch { return null; }
};
const storeBranding = (branding) => {
  if (branding) localStorage.setItem('tenant_branding', JSON.stringify(branding));
};
const clearBranding = () => localStorage.removeItem('tenant_branding');

// Axios interceptor to attach token
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Detect embedded mode (iframe inside Hub)
const isEmbedded = () => {
  try { return window.parent !== window; } catch { return false; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [embedded, setEmbedded] = useState(false);
  const [tenantBranding, setTenantBranding] = useState(getStoredBranding());

  useEffect(() => {
    setEmbedded(isEmbedded());
  }, []);

  // PostMessage bridge for token refresh (embedded mode)
  useEffect(() => {
    if (!embedded) return;

    const handleMessage = (event) => {
      const hubOrigins = [HUB_PREVIEW_URL, HUB_PROD_URL];
      if (!hubOrigins.includes(event.origin)) return;

      if (event.data?.type === 'cargodash.token') {
        // Hub sent a refreshed token — re-verify via our backend
        const refreshToken = async () => {
          try {
            const resp = await axios.get(`${API_URL}/api/sso/accept`, {
              params: { sso_token: event.data.token }
            });
            storeToken(resp.data.access_token);
            setUser(resp.data.user);
            if (resp.data.tenant_branding) {
              setTenantBranding(resp.data.tenant_branding);
              storeBranding(resp.data.tenant_branding);
            }
          } catch (err) {
            console.error('SSO token refresh failed:', err);
          }
        };
        refreshToken();
      }

      if (event.data?.type === 'cargodash.token.error') {
        // Hub session is dead — clear local session and redirect
        clearToken();
        clearBranding();
        setUser(null);
        const hubBase = event.origin || HUB_PROD_URL;
        const returnTo = encodeURIComponent(window.location.href);
        window.top.location.href = `${hubBase}/login?return_to=${returnTo}`;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [embedded]);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (err) {
      setUser(null);
      clearToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      storeToken(response.data.access_token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = formatApiError(detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const register = async (data) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/register/customer`, data);
      if (response.data.pending_approval) {
        return response.data;
      }
      storeToken(response.data.access_token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = formatApiError(detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // SSO user setter — called by SSOAccept page
  const setSSOUser = (accessToken, userData, branding) => {
    storeToken(accessToken);
    setUser(userData);
    if (branding) {
      setTenantBranding(branding);
      storeBranding(branding);
    }
  };

  // Impersonation: switch to customer view
  const startImpersonation = (accessToken, userData) => {
    localStorage.setItem('admin_token', getStoredToken());
    localStorage.setItem('admin_user', JSON.stringify(user));
    storeToken(accessToken);
    setUser(userData);
  };

  // Stop impersonation: restore admin view
  const stopImpersonation = async () => {
    const adminToken = localStorage.getItem('admin_token');
    if (adminToken) {
      localStorage.setItem('access_token', adminToken);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      // Re-fetch admin user from /auth/me
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (err) {
        setUser(null);
        clearToken();
      }
    }
  };

  // Request token refresh from Hub (embedded mode only)
  const requestTokenRefresh = () => {
    if (!embedded) return;
    const hubOrigin = window.location.ancestorOrigins?.[0] || HUB_PROD_URL;
    window.parent.postMessage({ type: 'cargodash.token.refresh' }, hubOrigin);
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearToken();
      clearBranding();
      setUser(null);
    }
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      setError(null);
      const response = await axios.post(`${API_URL}/api/auth/google/callback`, { session_id: sessionId });
      storeToken(response.data.access_token);
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMsg = formatApiError(detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (err) {
      // ignore
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    loginWithGoogle,
    handleGoogleCallback,
    checkAuth,
    refreshUser,
    setSSOUser,
    startImpersonation,
    stopImpersonation,
    requestTokenRefresh,
    embedded,
    tenantBranding,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
    isCustomer: user?.role === 'customer',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default AuthContext;
